// ============================================================================
// GsFeedbackButton — bulle d'acces rapide aux Commentaires (frere de GsNotificationBell)
// ============================================================================
// MASTER-DS-CHROME-FEEDBACK 2026-06-15 : la POC avait une bulle Commentaires a cote
// de la cloche. Bouton-icone PUR — pas de badge par defaut ; option showUnreadBadge
// (pastille + polling leger sur le meme endpoint, plafond 99+ par defaut, F-COQ-73).
//
// CAPABILITY-AWARE (mirror GsNotificationBell) : sonde /api/feedback/unread-count UNE
// fois ; si 404 (route feedback non montee) / 503 (migration en attente) / 401 / 403,
// la bulle SE MASQUE — aucun controle mort dans une app non branchee, aucun 500 surface.
//
// Pas de t() dans @gs/ui (pattern GsNotificationBellLabels) : libelles via props, defauts FR.
import { useCallback, useEffect, useId, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { api } from '@gs/api-client';

export interface GsFeedbackButtonLabels {
  /** aria-label du bouton quand count = 0 (defaut « Commentaires »). */
  button?: string;
  /** aria-label quand count > 0 (l appelant fournit la pluralisation i18n). */
  buttonUnread?: (count: number) => string;
}

export interface GsFeedbackButtonProps {
  /** Desactive la sonde tant que l'app n'est pas prete (ex: !user). */
  enabled?: boolean;
  /** Endpoint de sonde capability (defaut /api/feedback/unread-count — module @gs/feedback). */
  capabilityEndpoint?: string;
  /** Clic = naviguer vers la vue Commentaires de l'app. */
  onNavigate?: () => void;
  labels?: GsFeedbackButtonLabels;
  /** Affiche la pastille de reponses non lues (defaut false). */
  showUnreadBadge?: boolean;
  /** Plafond affiche « N+ » (defaut 99, PoC bureau TopBar). */
  unreadBadgeCap?: number;
  /** Intervalle de polling ms quand showUnreadBadge (defaut 60 s). */
  pollMs?: number;
}

interface UnreadCountResponse {
  count: number;
}

function formatUnreadBadge(count: number, cap: number): string {
  return count > cap ? `${cap}+` : String(count);
}

function isCapabilityError(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === 404 || status === 503 || status === 401 || status === 403;
}

export function GsFeedbackButton({
  enabled = true,
  capabilityEndpoint = '/api/feedback/unread-count',
  onNavigate,
  labels,
  showUnreadBadge = false,
  unreadBadgeCap = 99,
  pollMs = 60_000,
}: GsFeedbackButtonProps) {
  const unreadDescId = useId();
  const [capable, setCapable] = useState<boolean | null>(null); // null = pas encore sonde
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(() => {
    api.get<UnreadCountResponse>(capabilityEndpoint)
      .then((res) => {
        setCapable(true);
        if (showUnreadBadge) setUnreadCount(res.count ?? 0);
      })
      .catch((err: unknown) => {
        if (isCapabilityError(err)) setCapable(false);
      });
  }, [capabilityEndpoint, showUnreadBadge]);

  useEffect(() => {
    if (!enabled) return;
    if (!showUnreadBadge) {
      let active = true;
      api.get(capabilityEndpoint).then(
        () => { if (active) setCapable(true); },
        (err: unknown) => { if (active && isCapabilityError(err)) setCapable(false); },
      );
      return () => { active = false; };
    }
    refresh();
    const id = window.setInterval(refresh, pollMs);
    return () => window.clearInterval(id);
  }, [enabled, capabilityEndpoint, showUnreadBadge, pollMs, refresh]);

  if (!enabled || capable !== true) return null;

  const buttonLabel = labels?.button ?? 'Commentaires';
  const unreadDesc = unreadCount > 0 && labels?.buttonUnread
    ? labels.buttonUnread(unreadCount)
    : null;

  return (
    <button
      type="button"
      onClick={() => onNavigate?.()}
      aria-label={buttonLabel}
      aria-describedby={unreadDesc ? unreadDescId : undefined}
      className="touch-manipulation relative rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
    >
      {unreadDesc && (
        <span id={unreadDescId} className="sr-only">{unreadDesc}</span>
      )}
      <MessageSquare size={15} aria-hidden />
      {showUnreadBadge && unreadCount > 0 && (
        <span
          className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-brand-500 px-0.5 text-chip font-bold text-white"
          aria-hidden
        >
          {formatUnreadBadge(unreadCount, unreadBadgeCap)}
        </span>
      )}
    </button>
  );
}
